import { useEffect, useState } from "react";
import { addDays, isValid } from "date-fns";

import SelectMenu from "../controls/SelectMenu";
import DatePickerInput from "../controls/DatePickerInput";
import TextWithLabel from "../controls/TextWithLabel";
import Button from "../ui/Button";
import { formatDateStandard } from "../../shared/utility";
import {
  dropdownForPayload,
  fetchLocationsForDropdown,
  fetchLocationZonesForDropdown,
  normalizeDropdownOption,
} from "../../shared/locationDropdowns";
import { makePostApiCall } from "../../shared/makeApiCall";

function formatValidationErrors(data) {
  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat().join(" ");
  }
  return data?.message || "Could not promote this item.";
}

function parseDateValue(value) {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return isValid(parsed) ? parsed : null;
}

export default function PromoteReceivingToPackagingModal(props) {
  const {
    receivingItemId,
    onSuccess,
    onCancel,
    getPromoteReceivingDefaults,
    promoteReceivingToPackaging,
    logout,
  } = props;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [defaults, setDefaults] = useState(null);

  const [productOptions, setProductOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [location, setLocation] = useState(null);
  const [zone, setZone] = useState(null);
  const [packagingDate, setPackagingDate] = useState(null);
  const [bestBeforeDate, setBestBeforeDate] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setSubmitError(null);

      const [defaultsResult, productsResult, brandsResult, locationsResult] =
        await Promise.all([
          getPromoteReceivingDefaults(receivingItemId),
          makePostApiCall("fetchProductsForDropdown"),
          makePostApiCall("brandsForDropdown"),
          fetchLocationsForDropdown(),
        ]);

      if (cancelled) {
        return;
      }

      if (!defaultsResult?.success) {
        if (defaultsResult?.errorStatus === 401) {
          logout();
        }
        setSubmitError(
          defaultsResult?.data?.message || "Receiving item not found.",
        );
        setLoading(false);
        return;
      }

      const defaultsData = defaultsResult.data;
      setDefaults(defaultsData);
      setPackagingDate(
        parseDateValue(defaultsData.packaging_date) || new Date(),
      );
      setBestBeforeDate(
        parseDateValue(defaultsData.suggested_best_before_date),
      );

      if (productsResult.success) {
        setProductOptions(productsResult.data ?? []);
      } else if (productsResult.errorStatus === 401) {
        logout();
      }

      if (brandsResult.success) {
        setBrandOptions(brandsResult.data ?? []);
      } else if (brandsResult.errorStatus === 401) {
        logout();
      }

      if (locationsResult.success) {
        setLocationOptions(locationsResult.data ?? []);
      }

      const defaultLocation = normalizeDropdownOption(defaultsData.location);
      const defaultZone = normalizeDropdownOption(defaultsData.zone);
      const defaultBrand = normalizeDropdownOption(defaultsData.default_brand);

      if (defaultLocation) {
        setLocation(defaultLocation);
      }
      if (defaultZone) {
        setZone(defaultZone);
      }
      if (defaultBrand) {
        setSelectedBrand(defaultBrand);
      }

      setLoading(false);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [receivingItemId, getPromoteReceivingDefaults, logout]);

  useEffect(() => {
    if (!location?.value) {
      setZoneOptions([]);
      return;
    }

    let cancelled = false;

    async function loadZones() {
      const result = await fetchLocationZonesForDropdown(location);
      if (cancelled) {
        return;
      }
      setZoneOptions(result.success ? (result.data ?? []) : []);
    }

    loadZones();

    return () => {
      cancelled = true;
    };
  }, [location?.value]);

  const handleProductChange = async (e) => {
    const product = e.target.value;
    setSelectedProduct(product);
    setSubmitError(null);

    if (!product?.plu || !packagingDate) {
      return;
    }

    const productResult = await makePostApiCall(`products/plu/${product.plu}`);
    if (!productResult.success) {
      if (productResult.errorStatus === 401) {
        logout();
      }
      return;
    }

    const shelfLife = Number(productResult.data?.product?.shelf_life);
    if (shelfLife > 0 && packagingDate) {
      const suggested = addDays(packagingDate, shelfLife);
      if (isValid(suggested)) {
        setBestBeforeDate(suggested);
      }
    }
  };

  const handlePackagingDateChange = (e) => {
    const nextDate = e.target.value;
    setPackagingDate(nextDate);

    if (!selectedProduct?.plu || !nextDate) {
      return;
    }

    makePostApiCall(`products/plu/${selectedProduct.plu}`).then(
      (productResult) => {
        if (!productResult.success) {
          return;
        }
        const shelfLife = Number(productResult.data?.product?.shelf_life);
        if (shelfLife > 0 && nextDate) {
          const suggested = addDays(nextDate, shelfLife);
          if (isValid(suggested)) {
            setBestBeforeDate(suggested);
          }
        }
      },
    );
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    setZone(null);
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!selectedProduct?.value) {
      setSubmitError("Select a catalog product to ship as.");
      return;
    }
    if (!selectedBrand?.value) {
      setSubmitError("Select a brand.");
      return;
    }
    if (!location?.value) {
      setSubmitError("Select a location.");
      return;
    }
    if (!packagingDate || !bestBeforeDate) {
      setSubmitError("Packaging date and best-before date are required.");
      return;
    }

    setSubmitting(true);

    const payload = {
      receiving_assembly_item_id: receivingItemId,
      product_id: selectedProduct.value,
      brand_id: selectedBrand.value,
      packaging_date: formatDateStandard(packagingDate),
      best_before_date: formatDateStandard(bestBeforeDate),
      location: dropdownForPayload(location),
    };

    const zonePayload = dropdownForPayload(zone);
    if (zonePayload) {
      payload.zone = zonePayload;
    }

    const result = await promoteReceivingToPackaging(payload);

    setSubmitting(false);

    if (result.success) {
      onSuccess?.(result.data);
      return;
    }

    if (result.errorStatus === 401) {
      logout();
      return;
    }

    setSubmitError(formatValidationErrors(result.data));
  };

  const assemblyName = defaults?.assembly_item?.product_name;
  const manufacturerPlu = defaults?.assembly_item?.manufacturer_plu;

  return (
    <div className="max-h-[85vh] overflow-y-auto p-6 sm:p-8">
      <h3 className="text-lg font-semibold text-[#0F172A]">
        Create shippable unit from raw inventory
      </h3>
      <p className="mt-1 text-sm text-[#64748B]">
        Promote this received carton to sellable packaging. The same barcode
        will scan in shipment.
      </p>

      {loading ? (
        <p className="mt-6 text-sm text-[#64748B]">Loading…</p>
      ) : defaults ? (
        <>
          <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <TextWithLabel label="Barcode" text={defaults.barcode} />
              </div>
              <TextWithLabel label="Supplier assembly" text={assemblyName} />
              <TextWithLabel label="Supplier PLU" text={manufacturerPlu} />
              <TextWithLabel label="Lot number" text={defaults.lot_number} />
              <TextWithLabel label="Quantity" text={defaults.in_stock} />
              <TextWithLabel label="Box date" text={defaults.box_date || "—"} />
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <SelectMenu
              handleInputChange={handleProductChange}
              name="product_id"
              options={productOptions}
              value={selectedProduct}
              title="Catalog product *"
              placeholder="Select sellable SKU…"
              filter
              disabled={submitting}
            />
            <SelectMenu
              handleInputChange={(e) => setSelectedBrand(e.target.value)}
              name="brand_id"
              options={brandOptions}
              value={selectedBrand}
              title="Brand *"
              placeholder="Select brand…"
              disabled={submitting}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DatePickerInput
                label="Packaging date *"
                name="packaging_date"
                onChange={handlePackagingDateChange}
                value={packagingDate}
                labelOn
                dateFormat="yyyy-MM-dd"
                disabled={submitting}
              />
              <DatePickerInput
                label="Best before date *"
                name="best_before_date"
                onChange={(e) => setBestBeforeDate(e.target.value)}
                value={bestBeforeDate}
                labelOn
                dateFormat="yyyy-MM-dd"
                disabled={submitting}
              />
            </div>
            <SelectMenu
              handleInputChange={handleLocationChange}
              name="location"
              options={locationOptions}
              value={location}
              title="Location *"
              placeholder="Select location…"
              disabled={submitting}
            />
            <SelectMenu
              handleInputChange={(e) => setZone(e.target.value)}
              name="zone"
              options={zoneOptions}
              value={zone}
              title="Zone"
              placeholder={
                location?.value
                  ? "Select zone (optional)…"
                  : "Select location first…"
              }
              isClearable
              disabled={!location?.value || submitting}
            />
          </div>
        </>
      ) : null}

      {submitError ? (
        <p className="mt-4 text-sm font-medium text-[#DC2626]">{submitError}</p>
      ) : null}

      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={handleSubmit}
          loading={submitting}
          disabled={loading || !defaults || submitting}
        >
          Promote to sellable stock
        </Button>
      </div>
    </div>
  );
}
